import {
  Box,
  Card,
  FormLabel,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import InputControl from "../../../shared/components/InputControl";
import { Loader, LoadingButton } from "../../../shared";
import { AppContext } from "../../../store";
import { useRegionConfig } from "../hooks";
import styled from "@emotion/styled";
import { TenantInfoText } from "../components";

const ToggleButtonStyled = styled(ToggleButton)({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: "#1560D4",
  },
});

export const RegionConfig = () => {
  const {
    state,
    onCurrencyChange,
    onShowCurrencyChange,
    onCurrencyOptionChange,
    onOtherLanguageChange,
    onTimezoneChange,
    onSubmit,
  } = useRegionConfig({});
  const { appState } = useContext(AppContext);
  const { formData } = state;
  const languageOptions = state.languagesOptions || [];
  const currencyOptions = state.currenciesOptions || [];
  const timezoneOptions = appState?.globalData?.timezones || [];
  const selectedOtherLanguage = formData.languages[0] || null;
  const selectedCurrency =
    currencyOptions.find(
      (currency) => currency.value === formData.currency.currency_code,
    ) || null;

  if (state.isLoading) return <Loader />;

  const otherLanguages = languageOptions.filter((lang) => !lang.default);

  return (
    <Card component="form" onSubmit={onSubmit} sx={{ p: { xs: 2, sm: 2 } }}>
      <Stack gap={2.5}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1 }}>
          <Typography variant="body1" color="text.primary">
            Default Language
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {state.defaultLanguage?.name || "English"}
          </Typography>
        </Stack>

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <InputControl
              label="Other Language"
              type="dropdown"
              options={otherLanguages}
              value={selectedOtherLanguage}
              onChange={onOtherLanguageChange}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField {...params} label="Other Language" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputControl
              label="Primary Timezone"
              type="dropdown"
              options={timezoneOptions}
              value={
                timezoneOptions.find(
                  (option) => option.value === formData.timezone.code,
                ) || null
              }
              onChange={(_, timezone) => onTimezoneChange(timezone)}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField {...params} label="Primary Timezone" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={4} sm={6}>
            <InputControl
              label="Currency"
              type="dropdown"
              options={currencyOptions}
              value={selectedCurrency}
              onChange={onCurrencyOptionChange}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              getOptionLabel={(option) =>
                option.label ? `${option.label} (${option.value})` : ""
              }
              renderInput={(params) => (
                <TextField {...params} label="Currency" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2} sm={6}>
            <InputControl
              name="symbol"
              label="Currency Symbol"
              value={formData.currency.symbol}
              onChange={onCurrencyChange}
            />
          </Grid>
          <Grid item xs={12} md={7} lg={6}>
            <Stack
              flexDirection="row"
              alignItems="center"
              borderLeft={{ md: 1 }}
              justifyContent="space-between"
              borderColor={{ md: "divider" }}
            >
              <Box
                sx={{
                  pl: { md: "16px", xs: "0px" },
                  height: { sm: "48px", xs: "auto" },
                  overflowY: { sm: "visible", xs: "auto" },
                }}
              >
                <FormLabel sx={{ fontSize: "14px", fontWeight: 500 }}>
                  Show Currency Symbol
                </FormLabel>
                <TenantInfoText infoKey="showCurrencySymbol" />
              </Box>
              <InputControl
                type="switch"
                checked={formData.currency.show_currency}
                onChange={onShowCurrencyChange}
                labelPlacement="start"
              />
            </Stack>
          </Grid>
          {/*
          <Grid item xs={12} sm={6}>
            <InputControl
              name="currency_precision"
              label="Decimal Precision"
              type="number"
              value={formData.currency.currency_precision}
              onChange={onCurrencyChange}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputControl
              name="minimum_denomination"
              label="Minimum Denomination"
              type="number"
              value={formData.currency.minimum_denomination ?? ""}
              onChange={onCurrencyChange}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputControl
              name="rounding_mode"
              label="Rounding Strategy"
              value={formData.currency.rounding_mode}
              onChange={onCurrencyChange}
              disabled
            />
          </Grid>
          */}
        </Grid>

        <Stack gap={0.75}>
          <FormLabel sx={{ fontSize: "14px", fontWeight: 500 }}>
            Currency Symbol Position
          </FormLabel>
          <ToggleButtonGroup
            color="primary"
            size="small"
            exclusive
            value={formData.currency.symbol_placement}
            onChange={(_, placement) => {
              if (placement) {
                onCurrencyChange({
                  target: { name: "symbol_placement", value: placement },
                });
              }
            }}
          >
            <ToggleButtonStyled value="prefix" sx={{ minWidth: 196 }}>
              Prefix ({formData.currency.symbol || "$"}100.00)
            </ToggleButtonStyled>
            <ToggleButtonStyled value="suffix" sx={{ minWidth: 196 }}>
              Suffix (100.00{formData.currency.symbol || "$"})
            </ToggleButtonStyled>
          </ToggleButtonGroup>
          <TenantInfoText infoKey="symbolPlacement" />
        </Stack>

        <LoadingButton
          type="submit"
          variant="contained"
          loading={state.isSubmitting}
          disabled={!state.hasChanges}
          sx={{ ml: "auto", minWidth: 48 }}
        >
          Save
        </LoadingButton>
      </Stack>
    </Card>
  );
};
